'use client'
import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag } from '@/constants/hooks'
import { Offer, ProductData } from '@/services/dataTypes'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import ExpiryTag from './ExpiryTag'
import OfferDetailsToggle from './OfferDetailsToggle'
import { ArrowRight, Clock } from 'lucide-react'
import { copyToClipboard } from '../utils/clipboard'

interface Props {
    product: Offer | ProductData; domain: string; merchantHref: string
    merchant_name: string; merchant_logo: string; productDetailUrl?: string | null
}

const EventOfferCard = ({ product, merchantHref, domain, merchant_name, merchant_logo, productDetailUrl }: Props) => {
    const [copied, setCopied] = useState(false)
    const [revealed, setRevealed] = useState(false)

    const type = product?.offer_type?.name
    const imageSrc = type === 'product'
        ? getBaseImageUrl(domain, (product as any)?.product_image, '')
        : getBaseImageUrl(domain, merchant_logo, '')

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title || (product as any)?.offer_detail, discountPercent)
    const currencySymbol = getCurrencySymbol(product?.currency)
    const isCoupon = !!product?.coupon_code
    const duration = calculateOfferDuration(product?.end_date)
    const isLifetime = !product?.end_date || duration?.toLowerCase().includes('lifetime') || duration?.toLowerCase().includes('no expiry')

    const handleCopy = async (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation()
        if (!revealed) { setRevealed(true); return }
        await copyToClipboard(product?.coupon_code?.trim() ?? '')
        setCopied(true); setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="group relative bg-white rounded-[2rem] p-6 border border-slate-200/80 hover:border-[#5C7A5C]/40 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(45,62,45,0.12)] hover:-translate-y-1.5 transition-all duration-500 flex flex-col justify-between h-full overflow-hidden select-none">
            {/* Subtle background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8F0E8]/0 to-[#E8F0E8]/0 group-hover:from-[#E8F0E8]/50 group-hover:to-white transition-colors duration-500 -z-10" />

            {/* Top Section */}
            <div className="flex-1 flex flex-col">
                {/* Header Row: Floating badges */}
                <div className="flex items-center justify-between gap-1.5 mb-4 w-full min-w-0 flex-wrap sm:flex-nowrap">
                    {finalDiscountTag ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black bg-[#E8F0E8] text-[#2D3E2D] border border-[#5C7A5C]/20 shadow-2xs truncate">
                            {finalDiscountTag}
                        </span>
                    ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black uppercase tracking-widest bg-[#E8F0E8] text-[#5C7A5C] border border-[#5C7A5C]/20 shadow-2xs shrink-0">
                            VERIFIED
                        </span>
                    )}

                    {duration && !isLifetime ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-[#2D3E2D] bg-[#FAF7F2] border border-[#5C7A5C]/15 shrink-0 whitespace-nowrap ml-auto">
                            <Clock className="w-2.5 h-2.5 text-[#5C7A5C]" />
                            {duration}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] font-black text-[#5C7A5C] bg-[#E8F0E8] px-2 py-0.5 rounded-md border border-[#5C7A5C]/20 tracking-wider shrink-0 whitespace-nowrap ml-auto shadow-2xs">
                            ACTIVE
                        </span>
                    )}
                </div>

                {/* Image Area matching Theme 5 (w-32 h-32 rounded-3xl) */}
                <div className="relative w-32 h-32 bg-[#FAF7F2] rounded-3xl p-3.5 shadow-sm border border-[#5C7A5C]/10 group-hover:scale-105 transition-transform duration-500 flex items-center justify-center mx-auto mb-4 z-10">
                    <Image
                        src={imageSrc}
                        alt={discardHTMLTags(product?.offer_title) || merchant_name || 'Offer'}
                        width={200}
                        height={200}
                        className="object-contain w-full h-full p-2"
                        unoptimized
                    />
                </div>

                {/* Merchant Name */}
                <Link href={merchantHref} className="text-[10px] font-black text-[#5C7A5C] uppercase tracking-widest text-center mb-2 leading-none hover:underline">
                    {merchant_name}
                </Link>

                {/* Title */}
                {product?.is_detail === 1 && productDetailUrl ? (
                    <Link href={productDetailUrl}>
                        <h4 className="text-[14px] sm:text-[15px] font-bold text-slate-800 text-center leading-snug mb-2 hover:text-[#5C7A5C] transition-colors line-clamp-2"
                            style={{ fontFamily: 'Georgia, serif' }}>
                            {discardHTMLTags(product?.offer_title)}
                        </h4>
                    </Link>
                ) : (
                    <h4 className="text-[14px] sm:text-[15px] font-bold text-slate-800 text-center leading-snug mb-2 line-clamp-2"
                        style={{ fontFamily: 'Georgia, serif' }}>
                            {discardHTMLTags(product?.offer_title)}
                    </h4>
                )}

                {/* Price */}
                {originalPrice > 0 && salePrice > 0 && (
                    <div className="flex items-baseline justify-center gap-1.5 mb-3 leading-none">
                        <span className="text-base font-black text-[#2D3E2D]">
                            {currencySymbol}{salePrice.toFixed(2)}
                        </span>
                        <span className="text-xs text-slate-400 line-through font-medium">
                            {currencySymbol}{originalPrice.toFixed(2)}
                        </span>
                    </div>
                )}

                {(product as any)?.offer_detail && (
                    <p className="text-[11px] text-slate-500 text-center line-clamp-2 leading-relaxed mb-3">
                        {discardHTMLTags((product as any)?.offer_detail)}
                    </p>
                )}

                {/* Expiry + View Details */}
                <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-slate-100 text-xs">
                    <ExpiryTag endDate={product?.end_date} />
                    <OfferDetailsToggle
                        offer={product}
                        merchantHref={merchantHref}
                        imageSrc={product?.product_image ?? ''}
                        merchantImg={merchant_logo ?? ''}
                        domain={domain}
                        type="anchor"
                        modalType="simple"
                    />
                </div>
            </div>

            {/* Bottom Actions */}
            <div className="pt-2 flex flex-col gap-2">
                <OfferOutUrl
                    unique_id={product?.unique_id}
                    outUrl={product?.url}
                    merchantHref={merchantHref}
                    domain={domain}
                    customClass="w-full"
                >
                    <div
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-xs text-[#2D3E2D] shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                        style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}
                    >
                        <span>{isCoupon ? 'Get Code' : type === 'product' ? 'Buy Now' : 'Get Deal'}</span>
                        <ArrowRight size={13} />
                    </div>
                </OfferOutUrl>
            </div>
        </div>
    )
}

export default EventOfferCard

